import { defaultFirmaPersonRelationColumns } from "../../../../../config/firmaConfig";
import useActiveColumns from "../../../../../hooks/useActiveColumns";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import IngenRegistreringer from "../../../person/id/IngenRegistreringer";
import OpretNyButtonBlock from "../../../person/id/OpretNyButtonBlock";
import { useQuery } from "@apollo/client";
import { GET_FIRMA_BY_ID_PERSONRELATIONER } from "../../../../../api/firma/queries";
import { FirmaPersonRelationerField } from "../../../../../interfaces/firma";
import { PersonFirma } from "../../../../../interfaces/schema";
import { SortBy } from "../../../../../interfaces/table";
import { extractCurrentPersonArt } from "../../../../../util/person";
import useLocalStorageState from "use-local-storage-state";
import Spinner from "../../../../spinner/Spinner";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import ButtonDownload from "../../../../ButtonDownload";
import { orderBy } from "../../../../../util/orderBy";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";

const defaultSortBy: SortBy<FirmaPersonRelationerField>[] = [
  {
    direction: "desc",
    field: "periode.tilDatoInklusiv",
  },
  {
    direction: "desc",
    field: "periode.fraDato",
  },
];

export default function FirmaPersonRelationer({
  firmaId,
}: {
  firmaId: string;
}) {
  const [firmaPersonRelationColumns, setFirmaPersonRelationActiveColumns] =
    useActiveColumns(
      "firma-firmaperson-active-columns",
      defaultFirmaPersonRelationColumns,
    );

  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "firmaPersonRelationerSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );

  const { data, loading, error, refetch } = useQuery<{
    firma: {
      navn1: string;
      firmaNr: number;
      personFirma: PersonFirma[];
    };
  }>(GET_FIRMA_BY_ID_PERSONRELATIONER(), {
    variables: { id: firmaId },
  });
  const personFirma = data?.firma.personFirma.map((pf) => {
    const personart = extractCurrentPersonArt(pf.person);
    return {
      ...pf,
      person: {
        ...pf.person,
        personart: personart?.personartNrNavigation
          ? personart.personartNrNavigation.navn
          : "",
      },
    };
  });
  if (error) return <div>Fejl</div>;
  if (loading) return <Spinner />;
  if (!personFirma) return null;

  if (personFirma.length === 0) {
    return <IngenRegistreringer invalidateData={refetch} />;
  }

  // event handlers
  function downloadHandler() {
    if (personFirma) {
      tableToXLSX(firmaPersonRelationColumns, personFirma, {
        fileName: `personrelationer-for-${data?.firma.firmaNr}`,
        additionalData: {
          Firmanummer: data?.firma.firmaNr,
        },
      });
    }
  }

  return (
    <ModalProvider>
      <ButtonDownload
        onClick={downloadHandler}
        isDisabled={!personFirma.length}
      />
      <TableRoot>
        <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
          <>
            {getHeaderActiveColumns(firmaPersonRelationColumns, {
              defaultSortBy,
              setSortBy,
              sortBy,
            })}
          </>
        </TableHead>
        <TableBody>
          {orderBy(
            personFirma,
            sortBy.map((s) => s.field),
            sortBy.map((s) => s.direction),
          ).map((relation, i) => {
            return (
              <TableRow key={i} invalidateData={refetch}>
                <>
                  {firmaPersonRelationColumns
                    .filter((col) => col.active)
                    .map((col) => {
                      return (
                        <TD key={col.field} col={col} rowData={relation} />
                      );
                    })}
                </>
              </TableRow>
            );
          })}
        </TableBody>
      </TableRoot>
      <ColumnEditorModal
        defaultColumns={defaultFirmaPersonRelationColumns}
        columns={firmaPersonRelationColumns}
        setActiveColumns={setFirmaPersonRelationActiveColumns}
      />
      <OpretNyButtonBlock invalidateData={refetch} />
    </ModalProvider>
  );
}
